// 목록 페이지의 현재 page 번호를 URL query param(`?page=`)에 보존하는 순수 헬퍼.
// 상세로 이동했다가 뒤로 와도 같은 page로 돌아온다. 링크 공유와 새로고침에서도 page가 유지된다.
// React 연결부(useSearchParams)는 app/usePageParam.ts가 소유한다. 여기에는 로직만 둔다.

export const pageSearchParamKey = "page";

// 1-base page 번호. URL은 외부 입력이므로 비정상 값(비숫자, 0, 음수)은 1로 정규화한다.
export function parsePageParam(raw: string | null): number {
  if (raw === null) {
    return 1;
  }
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }
  return parsed;
}

// page를 query string에 기록한다. page<=1이면 키를 제거해 URL을 짧게 유지한다.
// 다른 파라미터(탭, 딥링크 등)는 그대로 보존한다. 입력 params는 변경하지 않는다.
export function writePageParam(
  params: URLSearchParams,
  page: number,
  key = pageSearchParamKey,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (page <= 1) {
    next.delete(key);
  } else {
    next.set(key, String(page));
  }
  return next;
}

// c6 DataTableSource 처럼 page 스냅샷과 goToPage를 가진 최소 형태.
export type PagedSource = {
  getSnapshot: () => { page: number };
  goToPage: (page: number) => Promise<void> | void;
};

// URL의 page가 바뀌었을 때(prev/next 클릭, 뒤로/앞으로) source를 그 page로 맞춘다.
// 이미 같은 page면 다시 조회하지 않는다.
export function applyUrlPageToSource(source: PagedSource, urlPage: number): void {
  if (source.getSnapshot().page !== urlPage) {
    void source.goToPage(urlPage);
  }
}

// 검색이나 필터 변경 등으로 source가 스스로 page를 1로 되돌리면 URL에도 반영한다.
// page가 다를 때만 기록한다. 그래서 URL→source→URL 순환이 생기지 않는다.
export function mirrorSourcePageToUrl(
  source: PagedSource,
  urlPage: number,
  setPage: (page: number) => void,
): void {
  const sourcePage = source.getSnapshot().page;
  if (sourcePage !== urlPage) {
    setPage(sourcePage);
  }
}

// prev/next 페이저에 쓰는 최소 형태. page/size/total 스냅샷을 가진다(c6 DataTableSource 가 충족).
export type PageRangeSource = {
  getSnapshot: () => { page: number; size: number; total: number };
};

// total/size 로 마지막 page(1-base, 최소 1)를 구한다.
export function lastPageOf(total: number, size: number): number {
  return Math.max(1, Math.ceil(total / size));
}

// 이전 page 로 이동을 요청한다(첫 페이지면 무시). 실제 적용은 URL→source(applyUrlPageToSource)가 한다.
export function goToPreviousPage(source: PageRangeSource, setPage: (page: number) => void): void {
  const { page } = source.getSnapshot();
  if (page > 1) {
    setPage(page - 1);
  }
}

// 다음 page 로 이동을 요청한다(마지막 페이지면 무시).
export function goToNextPage(source: PageRangeSource, setPage: (page: number) => void): void {
  const { page, size, total } = source.getSnapshot();
  if (page < lastPageOf(total, size)) {
    setPage(page + 1);
  }
}
